// The address from Google Places (TW-0084, 2026-09-08), the form's half.
//
// Chris, 2026-09-07: "what about using Google places for the address? this will be on mobile and not
// sure if it'll be clunky / slow but perhaps it makes sense?" · "could save people time and cost us
// pennies." The street box is the search box: from the third character a short list of matches drops
// under it, a tap fills street · city · state · ZIP (and the apartment line when Google knows it) and
// moves the cursor to the apartment box, and typing on without tapping posts exactly what was typed.
//
// THE BROWSER TALKS ONLY TO OUR API. No Google script, no Google request from the phone: the API
// asks Google with its own key from its own server (services/waiver-api/consolidated_api/address.py),
// biased to the SHOP's location, and the client's device never reaches Google. That is what keeps
// the consent page's "no third-party scripts" true, and it is why the consent page names the one
// thing that does go to Google: the letters typed into the street box.
//
// NOTHING HERE CAN BLOCK A SIGNING. No answer, a slow answer, no key, an old API with no route:
// every one of them is "no list", and the four boxes are the four boxes they have been since 2023.

import { API_URL } from "./config";

export type AddressSuggestion = { id: string; main: string; secondary: string };
export type PickedAddress = { address1: string; address2: string; city: string; state: string; zip: string };

export type AddressFields = { address1: string; address2: string; city: string; state: string; zip: string };

/** How long after the last keystroke the API is asked. */
export const SUGGEST_DEBOUNCE_MS = 250;
/** The fewest typed characters worth asking about. */
export const SUGGEST_MIN_CHARS = 3;
/** How long a fetch may take before the form stops waiting for it. */
const FETCH_TIMEOUT_MS = 5000;

/** One token per address search, so Google bills the keystrokes and the pick as one session. */
export function newAddressSession(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  } catch {
    /* an old WebView: fall through */
  }
  return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** Whether the list is worth asking for: enough typed in the street box, and the city and ZIP still
 *  empty. A phone's autofill lands all five boxes at once and a returning client's prefill (TW-0037)
 *  lands them before anyone types — both leave nothing to suggest, and neither ever sees a list.
 *
 *  2026-10-08 — AND NEVER FOR A SHOP IN HIPAA MODE (docs/HIPAA.md → What it loses: "No address suggestions on the form
 *  (they pass through Google, whose terms forbid health data under HIPAA)"). `/shop` says `hipaa: true` and the street box
 *  asks nothing, so no letter a client types leaves the phone for it; the four boxes are the four boxes. The API's address
 *  door answers such a shop empty before Google is asked (address.py), and that guard stands alone for the second or two
 *  before `/shop` lands, when the static config, which never carries the flag, is the shop on screen. */
export function shouldSuggest(form: Pick<AddressFields, "address1" | "city" | "zip">, shop?: { hipaa?: boolean } | null): boolean {
  if (shop?.hipaa === true) return false;
  return (form.address1 || "").trim().length >= SUGGEST_MIN_CHARS && !(form.city || "").trim() && !(form.zip || "").trim();
}

/** The form with the picked address in it. The four boxes are the pick's; the apartment line is
 *  filled only when the pick carries one AND the client has not typed one — theirs stays theirs. */
export function applyPick<T extends AddressFields>(form: T, picked: PickedAddress): T {
  return {
    ...form,
    address1: picked.address1 || form.address1,
    address2: (form.address2 || "").trim() ? form.address2 : picked.address2 || form.address2,
    city: picked.city || form.city,
    state: picked.state || form.state,
    zip: picked.zip || form.zip,
  };
}

function timeoutSignal(signal?: AbortSignal): AbortSignal | undefined {
  if (typeof AbortController === "undefined") return signal;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener("abort", () => controller.abort(), { once: true });
  }
  controller.signal.addEventListener("abort", () => clearTimeout(timer), { once: true });
  return controller.signal;
}

async function post(path: string, body: Record<string, string>, signal?: AbortSignal): Promise<any | null> {
  try {
    const response = await fetch(`${API_URL}/${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal: timeoutSignal(signal),
    });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

/** Ask for matches. Never throws; anything but a clean list is an empty list. */
export async function fetchAddressSuggestions(
  subdomain: string,
  input: string,
  session: string,
  signal?: AbortSignal
): Promise<AddressSuggestion[]> {
  const text = (input || "").trim();
  if (text.length < SUGGEST_MIN_CHARS) return [];
  const answer = await post("address-suggest", { subdomain, input: text, session }, signal);
  const list = answer && answer.ok === true && Array.isArray(answer.suggestions) ? answer.suggestions : [];
  return list
    .filter((s: any) => s && typeof s.id === "string" && s.id && typeof s.main === "string")
    .map((s: any) => ({ id: s.id, main: s.main, secondary: typeof s.secondary === "string" ? s.secondary : "" }));
}

/** Ask for the boxes behind one match. Never throws; anything but a clean address is null. */
export async function fetchAddressPick(
  subdomain: string,
  id: string,
  session: string,
  signal?: AbortSignal
): Promise<PickedAddress | null> {
  const answer = await post("address-pick", { subdomain, id, session }, signal);
  const a = answer && answer.ok === true ? answer.address : null;
  if (!a || typeof a !== "object") return null;
  const str = (v: unknown) => (typeof v === "string" ? v : "");
  const picked = { address1: str(a.address1), address2: str(a.address2), city: str(a.city), state: str(a.state), zip: str(a.zip) };
  if (!picked.address1 && !picked.zip) return null;
  return picked;
}
